<template>
  <div class="tw-p-6">
    <FeaturedFeedbackContainer
      v-if="featuredReviews.data?.length"
      :reviews="featuredReviews.data"
      :reviewsPerSlide="3"
    />

    <div v-if="summary.total" class="tw-mx-auto tw-max-w-6xl">
      <div class="tw-grid tw-grid-cols-1 tw-gap-x-10 tw-gap-y-4 sm:tw-grid-cols-12">
        <div class="tw-rounded-md sm:tw-col-span-5 md:tw-col-span-5 lg:tw-col-span-4">
          <div class="tw-sticky tw-top-0">
            <FeedbackSummary v-if="!customisation.hide_widget_total_score" :summary="summary" />

            <FeedbackFilter
              :filter="filters"
              :totalReviews="summary.total_nabooki_reviews"
              :activeFilter="activeFilter"
              v-on:valueSelect="onChangeFilter"
              v-if="!customisation.hide_widget_filters && activeTab === 'nabooki'"
              class="tw-mt-6"
            />

            <VerifiedReviewsBadge v-if="activeTab === 'nabooki'" class="tw-mt-5" />

            <template v-if="activeTab === 'google'">
              <GooglePlaces :places="googleReviews.places" />

              <GoogleReviewsBadge class="tw-mt-5" />
            </template>
          </div>
        </div>

        <div
          class="tab-container sm:tw-col-span-7 md:tw-col-span-7 lg:tw-col-span-8"
          :class="{ 'tw-animate-pulse': loading }"
        >
          <FeedbackTabs v-if="tabs.length" :tabs="tabs" v-model="activeTab" />

          <FeedbackContainer
            :reviews="allReviews.data"
            :nextPageReviewsUrl="reviews.links.next"
            @valueSelect="loadMoreReviews"
            v-if="activeTab === 'nabooki'"
          />

          <FeedbackContainer :reviews="googleReviews.reviews" v-if="activeTab === 'google'" />
        </div>
      </div>
    </div>

    <div v-else class="tw-col-span-12 tw-rounded-md">
      <NoReviews />
    </div>
  </div>
</template>

<script setup>
import { router, usePage } from '@inertiajs/vue3';
import Layout from '@Layout/Standalone/index.vue';
import { onMounted, reactive, ref } from 'vue';
import FeaturedFeedbackContainer from './Blocks/FeaturedFeedbackContainer.vue';
import FeedbackContainer from './Blocks/FeedbackContainer.vue';
import FeedbackFilter from './Blocks/FeedbackFilter.vue';
import FeedbackSummary from './Blocks/FeedbackSummary.vue';
import FeedbackTabs from './Blocks/FeedbackTabs.vue';
import GooglePlaces from './Blocks/GooglePlaces.vue';
import GoogleReviewsBadge from './Blocks/GoogleReviewsBadge.vue';
import NoReviews from './Blocks/NoReviews.vue';
import VerifiedReviewsBadge from './Blocks/VerifiedReviewsBadge.vue';

defineOptions({ layout: Layout });

const props = defineProps({
  business: Object,
  customisation: Object,
  summary: Object,
  reviews: Object,
  featuredReviews: Object,
  googleReviews: [Object, null],
  filters: {
    services: Array,
    ratings: Array,
  },
});

let loading = ref(false);
let tabs = ref([]);
let activeTab = ref('nabooki');
let allReviews = reactive(props.reviews);

const page = usePage();
const initialUrl = page.url;
const activeFilter = {
  service: null,
  ratings: [],
  commentsOnly: false,
};

onMounted(() => {
  props.filters.services.unshift({
    id: null,
    name: 'All',
  });

  if (props.summary.total_google_reviews > 0) {
    initTabs();
  }
});

const initTabs = () => {
  tabs.value = [
    {
      label: 'Nabooki',
      icon: asset('/assets/img/merchant_default_logo_thumb.png'),
      rating: props.summary.nabooki_reviews_average,
      count: props.summary.total_nabooki_reviews,
      origin: 'nabooki',
    },
    {
      label: 'Google',
      icon: asset('/assets/site/images/svg/google.svg'),
      rating: props.summary.google_reviews_average,
      count: props.summary.total_google_reviews,
      origin: 'google',
    },
  ];

  activeTab.value = props.summary.total_nabooki_reviews > 0 ? 'nabooki' : 'google';
};

const onChangeFilter = () => {
  fetchReviews(props.reviews.links.first);
};

const loadMoreReviews = () => {
  fetchReviews(props.reviews.links.next);
};

const fetchReviews = pageUrl => {
  loading.value = true;

  router.get(
    pageUrl,
    {
      filters: activeFilter,
    },
    {
      preserveState: true,
      preserveScroll: true,
      replace: true,
      only: ['reviews'],
      onSuccess: () => {
        // If first page is returned then reviews should not be appended
        if (props.reviews.meta.current_page === 1) {
          allReviews = props.reviews;

          return;
        }

        // Append reviews for subsequent page loads
        allReviews.data = [...allReviews.data, ...props.reviews.data];
      },
      onFinish: () => {
        loading.value = false;

        // Remove the query params from the url
        window.history.replaceState({}, page.title, initialUrl);
      },
    }
  );
};
</script>

<style scoped>
@media screen and (max-width: 639px) {
  .p-dialog-content {
    @apply !tw-p-0;
  }

  .header-title {
    @apply tw-px-0;
  }

  .tab-container {
    @apply tw-mt-6;
  }
}
</style>
